<template>
  <div class="ct-page">
    <APageHead title="교재 관리" sub="체계적인 교재 관리 시스템으로 효율적인 콘텐츠 운영을 지원합니다.">
      <template #actions>
        <ABtn icon="upload" @click="handleUploadFont">폰트 업로드</ABtn>
        <ABtn icon="translate" @click="openLanguageInfo">언어 코드표</ABtn>
        <ABtn icon="download" @click="handleDownload">엑셀 다운로드</ABtn>
        <!--<ABtn kind="primary" icon="add" @click="addNew">새 교재 등록</ABtn>-->
      </template>
    </APageHead>

    <!-- KPI -->
    <div class="ct-page__stats">
      <ACard><AStat :label="totalCountLabel" :value="totalCount || 0" unit="권" /></ACard>
      <ACard><AStat label="출판사 수" :value="publisherCount || 0" unit="곳" /></ACard>
      <ACard><AStat label="저자 수" :value="editorCount || 0" unit="명" /></ACard>
      <ACard><AStat label="선택됨" :value="selectedItems.length" unit="권" /></ACard>
    </div>

    <!-- 콘텐츠 카테고리 탭 -->
    <div class="p-tabs ct-page__tabs">
      <button
        v-for="tab in CATEGORY_TABS"
        :key="tab.id"
        type="button"
        :class="['p-tabs__tab', { 'is-active': activeTab === tab.id }]"
        @click="switchTab(tab.id)"
      >
        <i class="material-icons-round ct-page__tab-icon">{{ tab.icon }}</i>
        {{ tab.label }}
      </button>
    </div>

    <!-- 검색 -->
    <ACard overflow="visible">
      <SearchArea
        :search-fields="searchFields"
        :initial-values="initialSearchValues"
        :show-search-button="true"
        :show-download-button="false"
        @search="handleSearch"
      />
    </ACard>

    <!-- 교재 목록 -->
    <ACard>
      <template #title>
        <span class="ct-page__sec-title">{{ $t('label.27') }}</span>
      </template>
      <template #sub>{{ $t('txt.09') }}</template>
      <template #action>
        <ABtn
          v-if="textbooks && textbooks.length > 0"
          size="sm"
          kind="ghost"
          :icon="selectedItems.length === textbooks.length ? 'check_box' : 'check_box_outline_blank'"
          @click="toggleSelectAll"
        >{{ selectedItems.length === textbooks.length ? '전체 해제' : '전체 선택' }}</ABtn>
        <ABtn
          v-if="selectedItems.length > 0"
          size="sm"
          kind="ghost"
          icon="north"
          @click="bulkVersionUp('minor')"
        >선택 Minor 버전업 ({{ selectedItems.length }})</ABtn>
        <ABtn
          v-if="selectedItems.length > 0"
          size="sm"
          kind="danger"
          icon="north"
          @click="bulkVersionUp('major')"
        >선택 Major 버전업 ({{ selectedItems.length }})</ABtn>
        <APill kind="info">총 {{ totalCount }}건</APill>
      </template>

      <div v-if="statusFilterIgnored" class="ct-page__filter-warn">
        <i class="material-icons-round">error_outline</i>
        <span>
          서버가 <b>상태 필터</b>를 적용하지 않았습니다. 아래 목록은 걸러지지 않은 전체입니다 —
          상태 컬럼을 직접 확인해주세요.
        </span>
      </div>

      <AEmpty
        v-if="!textbooks || textbooks.length === 0"
        title="등록된 교재가 없어요"
        sub="새 교재를 등록하거나 필터를 변경해보세요."
      />

      <ATable
        v-else
        :columns="columns"
        :rows="textbooks"
        row-key="barcode"
        :sort-field="sortField"
        :sort-order="sortOrder"
        @sort="onSort"
      >
        <template #cell:select="{ row }">
          <input type="checkbox" :value="row.barcode" v-model="selectedItems" />
        </template>
        <template #cell:no="{ index }">
          <span class="ct-page__num">{{ getItemNumber(index) }}</span>
        </template>
        <template #cell:title="{ row }">
          <div class="ct-page__book">
            <img
              :src="row.cover_image_url || '/img/default-cover.png'"
              class="ct-page__book-cover"
              alt="cover"
              @error="onImageError"
            />
            <div class="ct-page__book-text">
              <div class="ct-page__book-title">
                <router-link :to="`/contents/editTextbook?barcode=${row.barcode}&page=${currentPage}`">
                {{ row.title }}
                </router-link>
              </div>
              <small class="ct-page__book-desc">{{ $stripHtmlTags ? $stripHtmlTags(row.description) : row.description }}</small>
            </div>
          </div>
        </template>
        <template #cell:publisher="{ row }">
          <span class="ct-page__cell">{{ row.publisher }}</span>
        </template>
        <template #cell:barcode="{ row }">
          <code class="ct-page__code">{{ row.barcode }}</code>
        </template>
        <template #cell:facets="{ row }">
          <div class="ct-page__facets">
            <span v-if="row.grade_label" class="ct-page__facet-grade">{{ row.grade_label }}</span>
            <span v-if="row.fields && row.fields.length" class="ct-page__facet-line">
              {{ row.fields.map((f) => f.name).join(" · ") }}
            </span>
            <span v-if="row.target_languages && row.target_languages.length" class="ct-page__facet-line ct-page__facet-line--lang">
              {{ row.target_languages.map((l) => l.name).join(" · ") }}
            </span>
            <!-- 승인 게이트(가이드 §2) 중 목록 응답으로 판정 가능한 두 개만 봅니다.
                 theme 은 목록에 실리지 않아 여기서 셀 수 없습니다 — 배지가 없다고 승인이
                 보장되지는 않으므로 문구도 "미입력"까지만 말합니다. -->
            <span v-if="missingFacetsOf(row).length" class="ct-page__facet-missing">
              <i class="material-icons-round">report_problem</i>
              {{ missingFacetsOf(row).join(" · ") }} 미입력
            </span>
          </div>
        </template>
        <template #cell:status="{ row }">
          <APill :kind="statusPill(row.status)" dot>{{ statusLabel(row.status) }}</APill>
        </template>
        <template #cell:series="{ row }">
          <span v-if="row.series_name" class="ct-page__series">
            {{ row.series_name }}
            <small v-if="row.series_volume">{{ row.series_volume }}권</small>
          </span>
          <span v-else class="ct-page__series-none">시리즈 미지정</span>
        </template>
        <template #cell:editor="{ row }">
          <span class="ct-page__cell">{{ row.editor_name }}</span>
        </template>
        <template #cell:created="{ row }">
          <span class="ct-page__mono">{{ row.created_at ? row.created_at.substring(0, 10) : '-' }}</span>
        </template>
        <template #cell:actions="{ row }">
          <div class="ct-page__actions">
            <router-link
              :to="`/contents/editTextbook?barcode=${row.barcode}&page=${currentPage}`"
              class="p-icon-btn"
              v-tooltip
              title="수정"
            >
              <i class="material-icons-round">edit</i>
            </router-link>
            <router-link
              :to="`/contents/editTextbookContents?barcode=${row.barcode}&page=${currentPage}`"
              class="p-icon-btn"
              v-tooltip
              title="콘텐츠 편집"
            >
              <i class="material-icons-round">description</i>
            </router-link>
            <router-link
              :to="`/contents/editWordsAndExpressions?barcode=${row.barcode}&page=${currentPage}`"
              class="p-icon-btn"
              v-tooltip
              title="단어·표현"
            >
              <i class="material-icons-round">spellcheck</i>
            </router-link>
          </div>
        </template>
      </ATable>

      <APagination
        v-if="totalCount > 0"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getTextbooks(); }"
      />
    </ACard>

    <!-- 폰트 업로드 모달 (자식) -->
    <FontUploadModal
      :is-visible="showFontUploadModal"
      @close="showFontUploadModal = false"
      @uploaded="onFontUploaded"
    />

    <!-- 지원 언어 코드표 모달 -->
    <AModal
      v-model="showLanguageModal"
      title="지원 언어 코드표"
      sub="퍼블리싱·앱개발 작업 시 이 코드 기준으로 파일/코드를 일치시켜 주세요."
      size="xl"
    >
      <!-- 변경 후 앱개발팀 전달 안내 -->
      <div v-if="langDirty" class="ct-lang__notice">
        <i class="material-icons-round">campaign</i>
        <span>언어 정보가 변경되었습니다. 새 <b>JSON 파일</b>을 내려받아 <b>앱개발팀</b>에 전달해 주세요.</span>
        <ABtn size="sm" kind="yellow" icon="download" @click="downloadLanguagesJson">JSON 다운로드</ABtn>
      </div>

      <div class="ct-lang__bar">
        <div class="ct-lang__count">총 <strong>{{ languages.length }}</strong>개 언어</div>
        <div class="ct-lang__bar-actions">
          <ABtn size="sm" kind="ghost" icon="download" @click="downloadLanguagesJson">JSON 다운로드</ABtn>
          <ABtn size="sm" kind="primary" icon="add" @click="openAddLang">언어 추가</ABtn>
        </div>
      </div>

      <!-- 추가 / 수정 폼 -->
      <div v-if="langForm" class="ct-lang__form">
        <div class="ct-lang__form-title">
          {{ langForm.mode === 'add' ? '언어 추가' : `언어 수정 — ${langForm.code}` }}
        </div>
        <div class="ct-lang__form-grid">
          <label class="ct-lang__field">
            <span>언어 코드</span>
            <input
              v-model.trim="langForm.code"
              :disabled="langForm.mode === 'edit'"
              class="p-input"
              placeholder="예: fr, zh-TW"
            />
          </label>
          <label v-for="f in nameFields" :key="f.key" class="ct-lang__field">
            <span>{{ f.label }}</span>
            <input v-model.trim="langForm.names[f.key]" class="p-input" :placeholder="`${f.label} 표시명`" />
          </label>
          <label class="ct-lang__field">
            <span>정렬순서 <small>(선택)</small></span>
            <input v-model="langForm.sort_order" type="number" class="p-input" placeholder="비우면 맨 뒤" />
          </label>
        </div>
        <div class="ct-lang__form-actions">
          <ABtn size="sm" kind="ghost" @click="cancelLangForm">취소</ABtn>
          <ABtn size="sm" kind="primary" :loading="langSaving" @click="saveLang">저장</ABtn>
        </div>
      </div>

      <div class="ct-lang__table">
        <ATable
          :columns="languageColumns"
          :rows="languages"
          row-key="code"
          striped
          bordered
        >
          <template #cell:_no="{ index }">{{ index + 1 }}</template>
          <template #cell:_actions="{ row }">
            <div class="ct-lang__row-actions">
              <ABtn size="sm" kind="ghost" icon="edit" icon-only @click="openEditLang(row)" />
              <ABtn size="sm" kind="danger" icon="delete" icon-only @click="deleteLang(row)" />
            </div>
          </template>
          <template #empty>지원 언어 정보를 불러오지 못했습니다.</template>
        </ATable>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="showLanguageModal = false">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import SearchArea from "@/components/SearchArea.vue";
import FontUploadModal from "@/components/FontUploadModal.vue";
import * as XLSX from "xlsx";
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
// 상태 라벨·색은 홈진열 화면과 같은 정의를 씁니다. 화면마다 따로 두면 값이 갈립니다.
import { textbookStatusLabel, textbookStatusPill } from "@/utils/homeSection";
import { apiErrorMessage } from "@/utils/apiError";

const CATEGORY_TABS = [
  { id: "textbook", label: "교재", icon: "menu_book", route: "ContentsTextbooks" },
  { id: "audiobook", label: "오디오북", icon: "audio_file", route: "ContentsAudiobooks" },
  { id: "video", label: "동영상", icon: "video_library", route: "ContentsVideos" },
];

// 언어 호칭 컬럼 라벨. 여기에 없는 키는 대문자 코드로 자동 표시되므로 언어 추가 시 코드 수정 불필요.
const LANG_LABELS = {
  ko: "한국어",
  en: "영어",
  ja: "일본어",
  vi: "베트남어",
  zh: "중국어",
  ru: "러시아어",
};

export default {
  name: "ContentsTextbooks",
  components: { SearchArea, FontUploadModal, APageHead, ABtn, ACard, AStat, APill, ATable, AModal, AEmpty, APagination },
  data() {
    // 시리즈·분야·언어 관리 모달의 [소속 교재 보기]가 새 탭으로 ?series_idx=N / ?field_idx=N /
    // ?language_idx=N 을 열고 들어옵니다. 비정수가 search_params에 들어가면 서버 오류이므로 정수만 통과시킵니다.
    const initialSearchValues = {};
    ["series_idx", "field_idx", "language_idx"].forEach((key) => {
      const v = Number(this.$route?.query?.[key]);
      if (Number.isInteger(v) && v > 0) initialSearchValues[key] = v;
    });

    return {
      CATEGORY_TABS,
      initialSearchValues,
      textbooks: [],
      showFontUploadModal: false,
      showLanguageModal: false,
      languages: [],
      langForm: null, // null=목록만, {mode:'add'|'edit', code, names, sort_order}
      langSaving: false,
      langDirty: false, // 변경 후 JSON 미전달 상태 (배너 표시용)
      activeTab: "textbook",
      currentPage: 1,
      itemsPerPage: 10,
      totalCount: 0,
      searchValues: { ...initialSearchValues },
      selectedItems: [],
      sortField: "",
      sortOrder: "asc",
      searchFields: [
        { key: "title", placeholder: this.$t("label.14") },
        { key: "publisher", placeholder: this.$t("label.29") },
        { key: "barcode", placeholder: this.$t("label.28") },
        // placeholder가 value="" 인 첫 옵션(= 전체)으로 렌더됩니다.
        // 분류 미지정 교재가 사라지면 안 되므로 기본값은 항상 "전체"입니다. 옵션은 서버에서 동적 조달.
        { key: "grade", type: "select", placeholder: "전체 단계", options: [] },
        { key: "field_idx", type: "select", placeholder: "전체 분야", options: [] },
        { key: "language_idx", type: "select", placeholder: "전체 언어", options: [] },
        { key: "series_idx", type: "select", placeholder: "전체 시리즈", options: [] },
        // 승인·반려가 관리자 전용이 되면서(가이드 §3) 승인 대기건을 찾는 경로가 여기뿐입니다.
        // 허용값 4개 정확 일치이며, 그 밖의 값은 서버가 400 을 냅니다(가이드 §1) —
        // 조용히 무시하는 grade 필터와 달라서, 값은 반드시 이 목록에서만 나와야 합니다.
        {
          key: "status",
          type: "select",
          placeholder: "전체 상태",
          options: [
            { label: "승인요청", value: "request" },
            { label: "승인완료", value: "approval" },
            { label: "거절", value: "rejection" },
            { label: "미요청", value: "none" },
          ],
        },
      ],
      statusFilterIgnored: false,
      columns: [
        { key: "select", label: "", align: "center", width: 40 },
        { key: "no", label: this.$t("label.18"), align: "center", width: 60 },
        { key: "title", label: this.$t("label.14"), width: 360, sortable: true },
        { key: "publisher", label: this.$t("label.29"), wrap: true, width: 140 },
        { key: "barcode", label: this.$t("label.28"), width: 140 },
        { key: "facets", label: "분류", wrap: true, width: 170 },
        { key: "status", label: "상태", align: "center", width: 96 },
        { key: "series", label: "시리즈", wrap: true, width: 150 },
        { key: "editor", label: "저작권자", wrap: true, width: 130 },
        { key: "created", label: this.$t("label.48"), width: 110 },
        { key: "actions", label: this.$t("label.19"), align: "right", width: 130 },
      ],
    };
  },
  computed: {
    userInfo() { return this.$store.state.auth.user_info || {}; },
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
    // total_count 는 필터 기준으로 계산됩니다(가이드 §1). 상태 필터를 걸어놓고
    // "전체 교재 35권"이라고 적으면 dev 전체가 50권인데도 35권이 전부인 줄 읽습니다.
    totalCountLabel() {
      return Object.keys(this.searchValues).length ? "검색 결과" : "전체 교재";
    },
    publisherCount() {
      return new Set(this.textbooks.map((i) => i.publisher)).size;
    },
    editorCount() {
      return new Set(this.textbooks.map((i) => i.editor_name)).size;
    },
    // 응답의 code 외 키를 호칭 컬럼으로 자동 구성 (호칭 언어 추가돼도 코드 수정 불필요)
    languageColumns() {
      const base = [
        { key: "_no", label: "번호" },
        { key: "code", label: "언어 코드" },
      ];
      const sample = this.languages[0];
      if (!sample) return base;
      const nameCols = Object.keys(sample)
        .filter((k) => k !== "code")
        .map((k) => ({ key: k, label: LANG_LABELS[k] || k.toUpperCase() }));
      return [...base, ...nameCols, { key: "_actions", label: "관리" }];
    },
    // 추가/수정 폼의 호칭 입력 필드 (LANG_LABELS 기준)
    nameFields() {
      return Object.entries(LANG_LABELS).map(([key, label]) => ({ key, label }));
    },
  },
  mounted() {
    const pageFromQuery = parseInt(this.$route.query.page);
    if (pageFromQuery && pageFromQuery > 0) this.currentPage = pageFromQuery;
    this.getSeries();
    this.getFilterOptions();
    if (this.userInfo && this.userInfo.name) this.getTextbooks();
  },
  methods: {
    switchTab(id) {
      const tab = CATEGORY_TABS.find((t) => t.id === id);
      if (tab) this.$router.push({ name: tab.route });
    },
    async getTextbooks() {
      try {
        const params = {
          limit: this.itemsPerPage,
          page: this.currentPage,
          search_params: JSON.stringify(this.searchValues),
        };
        if (this.sortField) {
          params.sort_field = this.sortField;
          params.sort_order = this.sortOrder;
        }
        const res = await this.$axios({
          method: "get",
          url: "/textbook/editor/textbooks",
          params,
          loadingBar: true,
        });
        if (res.status === 200) {
          this.textbooks = res.data.items;
          this.totalCount = res.data.total_count;
          this.selectedItems = [];
          this.checkStatusFilterApplied();
        }
      } catch (error) {
        console.error("교재 목록 조회 실패:", error);
        // 조회가 실패하면 직전 목록이 화면에 그대로 남습니다. 상태 필터를 쓰는 지금은
        // 그게 위험합니다 — 승인 대기 목록인 줄 알고 승인된 교재를 보게 되는,
        // 서버가 400 을 내면서까지 막으려던 바로 그 상황이기 때문입니다(가이드 §1).
        // 그래서 목록을 비우고 사유를 노출합니다.
        this.textbooks = [];
        this.totalCount = 0;
        this.selectedItems = [];
        this.statusFilterIgnored = false;
        this.$notifyVue({
          type: "error",
          title: "교재 목록을 불러오지 못했습니다",
          text: apiErrorMessage(error, "잠시 후 다시 시도해주세요."),
        });
      }
    },
    // 상태 필터를 걸었는데 응답에 다른 상태가 섞여 있으면 서버가 그 키를 무시한 것입니다.
    // dev 는 2026-08-28 에 이 필터가 들어갔지만 운영은 아직 미배포라, 운영 빌드에서는
    // 구버전 서버가 모르는 키로 보고 조용히 무시합니다(grade 필터와 같은 동작).
    // 그대로 두면 "승인 대기 35건"을 전체 50건으로 잘못 읽으므로 화면에 밝힙니다.
    // 빈 목록은 판정하지 않습니다 — 정말 0건일 수도 있어 구분이 안 됩니다.
    checkStatusFilterApplied() {
      const wanted = this.searchValues.status;
      if (!wanted || !this.textbooks.length) {
        this.statusFilterIgnored = false;
        return;
      }
      this.statusFilterIgnored = this.textbooks.some(
        (t) => this.normalizeStatus(t.status) !== wanted
      );
    },
    // 미요청은 서버가 빈 문자열/null 로 주고 필터에서는 'none' 으로 고릅니다.
    normalizeStatus(status) {
      return status || "none";
    },
    statusLabel(status) {
      return textbookStatusLabel(status);
    },
    statusPill(status) {
      return textbookStatusPill(status);
    },
    // 목록 응답으로 확인 가능한 승인 게이트 항목만 셉니다(가이드 §1·§2).
    missingFacetsOf(row) {
      const missing = [];
      if (!row.grade) missing.push("학습단계");
      if (!(row.fields && row.fields.length)) missing.push("도서분야");
      return missing;
    },
    // 컬럼 헤더 정렬: 1차 오름차순 → 2차 내림차순 → 3차 기본 정렬 복귀
    onSort(field) {
      if (this.sortField !== field) {
        this.sortField = field;
        this.sortOrder = "asc";
      } else if (this.sortOrder === "asc") {
        this.sortOrder = "desc";
      } else {
        this.sortField = "";
        this.sortOrder = "asc";
      }
      this.currentPage = 1;
      this.getTextbooks();
    },
    addNew() { this.$router.push({ name: "EditTextbook" }); },
    handleSearch(values) {
      // 기존 title/publisher/barcode 키는 그대로 보존하고 분류 키만 손봅니다.
      // idx 계열은 정수여야 하며(비정수 → 서버 오류), 값이 없으면 키 자체를 빼야 "전체"가 됩니다.
      const next = { ...values };
      ["series_idx", "field_idx", "language_idx"].forEach((key) => {
        if (next[key] === "" || next[key] == null) delete next[key];
        else next[key] = Number(next[key]);
      });
      // grade는 문자열 code 정확 일치라 빈 문자열을 보내면 전부 걸러집니다. status 도 같습니다.
      if (!next.grade) delete next.grade;
      if (!next.status) delete next.status;

      this.searchValues = next;
      this.currentPage = 1;
      this.getTextbooks();
    },
    // 학습단계·도서분야·대상언어 필터 옵션 일괄 조회. idx·표시명은 하드코딩하지 않습니다.
    async getFilterOptions() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/filters",
          params: { lang: "ko" },
        });
        if (res.status === 200 && res.data) {
          const setOptions = (key, options) => {
            const field = this.searchFields.find((f) => f.key === key);
            if (field) field.options = options;
          };
          setOptions("grade", (res.data.grades || []).map((g) => ({ label: g.name, value: g.code })));
          setOptions("field_idx", (res.data.fields || []).map((f) => ({ label: f.name, value: f.idx })));
          setOptions("language_idx", (res.data.languages || []).map((l) => ({ label: l.name, value: l.idx })));
        }
      } catch (error) {
        console.error("분류 필터 옵션 조회 실패:", error);
      }
    },
    // 시리즈 필터 옵션. 인증 불필요한 공개 API이며 목록 전체를 받습니다.
    async getSeries() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/textbook/series",
        });
        if (res.status === 200 && Array.isArray(res.data)) {
          const field = this.searchFields.find((f) => f.key === "series_idx");
          if (field) field.options = res.data.map((s) => ({ label: s.name, value: s.idx }));
        }
      } catch (error) {
        console.error("시리즈 목록 조회 실패:", error);
      }
    },
    toggleSelectAll() {
      this.selectedItems =
        this.selectedItems.length === this.textbooks.length
          ? []
          : this.textbooks.map((t) => t.barcode);
    },
    async bulkVersionUp(type) {
      if (this.selectedItems.length === 0) {
        this.$notifyVue({ type: "warning", title: "선택된 교재 없음", text: "버전업할 교재를 선택해주세요." });
        return;
      }
      const label = type === "major" ? "Major" : "Minor";
      const endpoint = type === "major" ? "majorVersionUp" : "minorVersionUp";
      const confirm = await swal.fire({
        title: `선택한 ${this.selectedItems.length}권을 ${label} 버전업 하시겠습니까?`,
        text: "검토/테스트용 버전이 생성됩니다.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("btn.22"),
        cancelButtonText: this.$t("btn.23"),
      });
      if (!confirm.isConfirmed) return;

      const barcodes = [...this.selectedItems];
      let successCount = 0;
      const failed = [];
      for (const barcode of barcodes) {
        try {
          const res = await this.$axios({
            method: "post",
            url: `/textbook/editor/postStatus/${barcode}/${endpoint}`,
            data: {
              user_idx: this.userInfo.idx,
              user_name: this.userInfo.name,
              user_email: this.userInfo.email,
            },
            loadingBar: true,
          });
          if (res?.status === 200) successCount++;
          else failed.push(barcode);
        } catch (e) {
          console.error(`버전업 실패 (${barcode}):`, e);
          failed.push(barcode);
        }
      }

      if (failed.length === 0) {
        this.$notifyVue({ type: "success", title: "버전업 완료", text: `${successCount}권의 ${label} 버전이 업데이트되었습니다.` });
      } else if (successCount > 0) {
        this.$notifyVue({ type: "warning", title: "일부 완료", text: `${successCount}권 성공, ${failed.length}권 실패` });
      } else {
        this.$notifyVue({ type: "danger", title: "버전업 실패", text: "선택한 교재의 버전업에 실패했습니다." });
      }

      await this.getTextbooks();
    },
    async handleDownload() {
      try {
        const params = {
          editor_idx: this.userInfo.idx,
          limit: 9999,
          page: 1,
          search_params: JSON.stringify(this.searchValues),
        };
        if (this.sortField) {
          params.sort_field = this.sortField;
          params.sort_order = this.sortOrder;
        }
        const res = await this.$axios({
          method: "get",
          url: "/textbook/editor/textbooks",
          params,
          loadingBar: true,
        });
        if (res.status === 200) {
          const all = res.data.items;
          const excelData = all.map((item, index) => ({
            번호: all.length - index,
            제목: item.title,
            출판사: item.publisher,
            설명: item.description,
            생성일: item.created_at,
          }));
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(excelData);
          ws["!cols"] = [{ wch: 10 }, { wch: 30 }, { wch: 20 }, { wch: 50 }, { wch: 20 }];
          XLSX.utils.book_append_sheet(wb, ws, "교재 목록");
          XLSX.writeFile(wb, `교재_목록_${new Date().toISOString().split("T")[0]}.xlsx`);
        }
      } catch (error) {
        console.error("Error downloading excel:", error);
      }
    },
    handleUploadFont() { this.showFontUploadModal = true; },
    async openLanguageInfo() {
      this.showLanguageModal = true;
      this.cancelLangForm();
      if (!this.languages.length) await this.fetchLanguages();
    },
    async fetchLanguages() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/common/languages",
          loadingBar: true,
        });
        this.languages = res.data || res || []; // 순수 JSON 배열
      } catch (e) {
        this.languages = []; // 실패 시 ATable #empty 노출
      }
    },
    openAddLang() {
      const names = {};
      this.nameFields.forEach((f) => { names[f.key] = ""; });
      this.langForm = { mode: "add", code: "", names, sort_order: "" };
    },
    openEditLang(row) {
      const names = {};
      this.nameFields.forEach((f) => { names[f.key] = row[f.key] || ""; });
      this.langForm = { mode: "edit", code: row.code, names, sort_order: "" };
    },
    cancelLangForm() {
      this.langForm = null;
    },
    downloadLanguagesJson() {
      if (!this.languages.length) {
        swal.fire("안내", "다운로드할 언어 정보가 없습니다.", "info");
        return;
      }
      const json = JSON.stringify(this.languages, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `languages_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.langDirty = false; // 전달용 파일을 받았으므로 대기 상태 해제
    },
    // 추가/수정/삭제 후: 목록 갱신 + 앱개발팀 전달 안내
    async afterLangChange(actionText) {
      this.langDirty = true;
      await this.fetchLanguages();
      const r = await swal.fire({
        icon: "info",
        title: "앱개발팀 전달 필요",
        html: `언어 정보가 <b>${actionText}</b>되었습니다.<br>`
          + "변경된 <b>새 JSON 파일</b>을 내려받아 <b>앱개발팀</b>에 전달해 주세요.",
        showCancelButton: true,
        confirmButtonText: "JSON 다운로드",
        cancelButtonText: "나중에",
      });
      if (r.isConfirmed) this.downloadLanguagesJson();
    },
    async saveLang() {
      const f = this.langForm;
      if (!f) return;
      if (f.mode === "add") {
        if (!f.code) { swal.fire("입력 확인", "언어 코드를 입력하세요.", "warning"); return; }
        const empty = this.nameFields.find((nf) => !f.names[nf.key]);
        if (empty) { swal.fire("입력 확인", `${empty.label} 표시명을 입력하세요.`, "warning"); return; }
      }
      this.langSaving = true;
      try {
        const data = { names: f.names };
        if (f.sort_order !== "" && f.sort_order != null) data.sort_order = Number(f.sort_order);
        let actionText;
        if (f.mode === "add") {
          data.code = f.code;
          await this.$axios({ method: "post", url: "/common/languages", data, loadingBar: true });
          actionText = "추가";
        } else {
          await this.$axios({
            method: "put",
            url: `/common/languages/${encodeURIComponent(f.code)}`,
            data,
            loadingBar: true,
          });
          actionText = "수정";
        }
        this.cancelLangForm();
        await this.afterLangChange(actionText);
      } catch (e) {
        this.showLangError(e);
      } finally {
        this.langSaving = false;
      }
    },
    async deleteLang(row) {
      const ok = await swal.fire({
        title: "언어 삭제",
        html: `'<b>${row.code}</b>' 언어를 삭제하시겠습니까?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "삭제",
        cancelButtonText: "취소",
        confirmButtonColor: "#d33",
      });
      if (!ok.isConfirmed) return;
      try {
        await this.$axios({
          method: "delete",
          url: `/common/languages/${encodeURIComponent(row.code)}`,
          loadingBar: true,
        });
        if (this.langForm && this.langForm.code === row.code) this.cancelLangForm();
        await this.afterLangChange("삭제");
      } catch (e) {
        this.showLangError(e);
      }
    },
    showLangError(e) {
      const status = e && e.response && e.response.status;
      const map = {
        400: "변경할 내용이 없습니다.",
        401: "권한이 없습니다. 관리자로 로그인해 주세요.",
        404: "대상 언어를 찾을 수 없습니다.",
        409: "이미 존재하는 언어 코드입니다.",
      };
      const msg = map[status]
        || (e && e.response && e.response.data && e.response.data.message)
        || "처리 중 오류가 발생했습니다.";
      swal.fire("오류", msg, "error");
    },
    onFontUploaded(count) {
      this.$notifyVue({ type: "success", title: `${count}개 폰트 파일이 업로드되었습니다.` });
    },
    onImageError(e) {
      e.target.onerror = null;
      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="64"%3E%3Crect fill="%23F0E8D2" width="48" height="64"/%3E%3C/svg%3E';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ct-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__tabs { align-self: flex-start; }
  &__tab-icon { font-size: 14px !important; line-height: 1; }

  &__sec-title { font-size: 13px; font-weight: 800; color: $p-ink; }

  &__num { font-family: $p-font-mono; color: $p-ink-mid; font-weight: 700; }

  &__book {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  &__book-cover {
    width: 40px;
    height: 54px;
    object-fit: cover;
    border-radius: $p-radius-sm;
    box-shadow: $p-shadow-sm;
    flex-shrink: 0;
  }

  // 컬럼 너비 360 - 좌우 패딩(28) - 커버(40) - gap(10) ≈ 282
  &__book-text {
    min-width: 0;
    width: 100%;
    max-width: 280px;
    overflow: hidden;
  }

  &__book-title {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__book-desc {
    display: block;
    margin-top: 2px;
    color: $p-ink-soft;
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }

  &__cell {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    max-width: 100%;
  }

  &__code {
    display: inline-block;
    padding: 3px 8px;
    background: $p-panel-alt;
    border: 1px solid $p-line;
    border-radius: 5px;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink2;
    font-weight: 700;
  }

  &__mono {
    font-family: $p-font-mono;
    color: $p-ink-soft;
    font-size: 11px;
  }

  &__series {
    font-size: 12px;
    font-weight: 700;
    color: $p-ink2;

    small {
      display: inline-block;
      margin-left: 4px;
      padding: 1px 5px;
      border-radius: 4px;
      background: $p-panel-alt;
      font-weight: 700;
      color: $p-ink-mid;
    }
  }

  // 시리즈·분류 미지정은 오류가 아니라 정상 상태이므로 경고색을 쓰지 않습니다.
  &__series-none {
    font-size: 11px;
    color: $p-ink-softer;
  }

  &__facets {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__facet-grade {
    align-self: flex-start;
    padding: 1px 6px;
    border-radius: 4px;
    background: $p-point-soft;
    font-size: 11px;
    font-weight: 800;
    color: $p-ink2;
  }

  &__facet-line {
    font-size: 11px;
    color: $p-ink-mid;

    &--lang { color: $p-ink-soft; }
  }

  // 분류 미지정(정상 상태)과 달리 이건 "승인이 막히는 사유"라 경고색을 씁니다.
  &__facet-missing {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 3px;
    margin-top: 2px;
    padding: 1px 6px;
    border-radius: 4px;
    background: $p-warn-soft;
    font-size: 11px;
    font-weight: 700;
    color: $p-warn-ink;

    > i { font-size: 12px !important; }
  }

  &__filter-warn {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 8px 10px;
    border: 1px solid $p-err;
    border-radius: $p-radius-sm;
    background: $p-err-soft;
    font-size: 12px;
    color: $p-ink;

    > i { color: $p-err; font-size: 16px; }
    span { flex: 1; }
  }

  &__actions {
    display: inline-flex;
    gap: 4px;
    justify-content: flex-end;
  }
}

.ct-lang__notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid $p-warn;
  border-radius: $p-radius-sm;
  background: rgba($p-warn, 0.1);
  font-size: 13px;
  color: $p-ink;

  i { color: $p-warn; font-size: 18px; }
  span { flex: 1; }
  b { font-weight: 800; }
}

.ct-lang__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.ct-lang__bar-actions {
  display: inline-flex;
  gap: 6px;
}

.ct-lang__count {
  font-size: 13px;
  color: $p-ink-mid;

  strong { color: $p-ink; font-weight: 800; }
}

.ct-lang__form {
  margin-bottom: 14px;
  padding: 14px;
  border: 1px solid $p-line;
  border-radius: $p-radius-sm;
  background: $p-panel-alt;

  &-title { font-size: 13px; font-weight: 800; color: $p-ink; margin-bottom: 10px; }

  &-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 10px;
  }

  &-actions {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 12px;
  }
}

.ct-lang__field {
  display: flex;
  flex-direction: column;
  gap: 4px;

  span { font-size: 12px; font-weight: 700; color: $p-ink-mid; }
  small { font-weight: 400; color: $p-ink-soft; }
}

.ct-lang__row-actions {
  display: inline-flex;
  gap: 4px;
}

// 목록 세로 스크롤 시 헤더(필드명) 상단 고정
.ct-lang__table {
  :deep(.a-table-wrap) {
    max-height: 60vh;
    overflow-y: auto;
  }

  :deep(.a-table__th) {
    position: sticky;
    top: 0;
    z-index: 2;
    // collapse 보더가 sticky에서 사라지는 것 방지 (하단 구분선 유지)
    box-shadow: inset 0 -1px 0 $p-line;
  }
}
</style>
